import {
  ButtonColor,
  ButtonShape,
  ButtonSize,
  CopyToClipboardButton,
  Divider,
  DropdownButton,
  DropdownItem,
  Icon,
  IconName,
  IconSize,
  IconVariant,
  Pagination,
  type PaginationGenerics,
  type SortGenerics,
  stringCompare,
  Table,
  TBody,
  TD,
  TextCell,
  TH,
  THead,
  Tooltip,
  TR,
  testIgnoreDiacritics,
  useLinkAttrs,
  useModalConfirmation,
  useNumberSearchRegExps,
  useSearchRegExps,
  useToastManager
} from "@quortex/front-common"
import { useNavigate, useSearch } from "@tanstack/react-location"
import { useMutation } from "@tanstack/react-query"
import { type FC, useEffect, useMemo } from "react"
import { useWatch } from "react-hook-form"
import { CdnStatus } from "@/components/CdnStatus"
import { SegmentListCell } from "@/components/SegmentListCell"
import { DefaultPageSize } from "@/const"
import { useWorkspacePath } from "@/hooks/navigation"
import { useDeleteCdn } from "@/queries/cdn"

const pageSize = DefaultPageSize

type TRow = {
  name: string
  pathwayId: string
  qoeStatus: api.switchns.Cdn["qoeStatus"]
  uuid: string
  segmentList: api.switchns.CdnSegment[]
  playUuid?: string | null
}

type SortKey = "name" | "pathwayId"

const defaultSortKey: SortKey = "name"

const nameToDefaultIsAscend = {
  name: true,
  pathwayId: true
} as const

const getSortableOptions = (key: SortKey) => ({
  defaultIsAscend: nameToDefaultIsAscend[key],
  defaultSortKey,
  key
})

const compareFunctionByCol: Record<SortKey, (a: string, b: string) => number> = {
  name: stringCompare,
  pathwayId: stringCompare
}

interface CdnTableProps {
  cdnList: api.switchns.PaginatedCdnList
}

const CdnTable: FC<CdnTableProps> = ({ cdnList }) => {
  const search = useSearch<PaginationGenerics & SortGenerics<SortKey>>()
  const page = search?.page ?? 1
  const searchValue = useWatch<{ search: string }>({ name: "search" })
  const searchRegExps = useSearchRegExps(searchValue)
  const numberSearchRegExps = useNumberSearchRegExps(searchValue)
  const navigate = useNavigate()

  useEffect(() => {
    if (cdnList.count === 0)
      navigate({
        to: `../segmentation`
      })
  }, [cdnList, navigate])

  const rows: TRow[] = useMemo(
    () =>
      cdnList.results?.map(cdn => {
        return {
          name: cdn.name,
          pathwayId: cdn.pathwayId,
          playUuid: cdn.playUuid,
          qoeStatus: cdn.qoeStatus,
          segmentList: cdn.segments,
          uuid: cdn.uuid
        }
      }),
    [cdnList.results]
  )

  const filteredRows = useMemo(() => {
    if (searchRegExps.length === 0) return rows
    return rows.filter(row =>
      searchRegExps.every(
        regExp =>
          testIgnoreDiacritics(regExp, row.name) || testIgnoreDiacritics(regExp, row.pathwayId)
      )
    )
  }, [searchRegExps, rows])

  const sortedRows = useMemo(() => {
    const { key, defaultIsAscend } = getSortableOptions(search.sort?.key || defaultSortKey)
    const isAscend = search.sort?.order ? search.sort?.order === "ascend" : defaultIsAscend
    const compareFunction = compareFunctionByCol[key]
    const compare = isAscend
      ? (a: TRow, b: TRow) => compareFunction(a[key], b[key])
      : (a: TRow, b: TRow) => compareFunction(b[key], a[key])
    return [...filteredRows].sort(compare)
  }, [filteredRows, search.sort?.key, search.sort?.order])

  return (
    <>
      <div className="overflow-hidden overflow-x-auto" data-testid="cdn-table">
        <Table>
          <THead>
            <TR>
              <TH scope="col" sortable={getSortableOptions("name")}>
                Name
              </TH>
              <TH scope="col" sortable={getSortableOptions("pathwayId")}>
                Pathway id
              </TH>
              <TH scope="col">Used in</TH>
              <TH scope="col">QoE status</TH>
              <TH scope="col"></TH>
            </TR>
          </THead>
          <TBody>
            {sortedRows.slice((page - 1) * pageSize, page * pageSize).map(row => (
              <Row
                key={row.uuid}
                numberSearchRegExps={numberSearchRegExps}
                row={row}
                searchRegExps={searchRegExps}
              />
            ))}
          </TBody>
        </Table>
      </div>

      <Pagination pageSize={pageSize} rowsNb={filteredRows.length} />
    </>
  )
}

type RowProps = { numberSearchRegExps: RegExp[]; searchRegExps: RegExp[]; row: TRow }

const Row = ({ row, searchRegExps }: RowProps) => {
  const workspacePath = useWorkspacePath()
  const linkAttrs = useLinkAttrs(`${workspacePath}/cdn/${row.uuid}`)
  const { openConfirmationModal } = useModalConfirmation()
  const toastManager = useToastManager()
  const navigate = useNavigate()

  const { mutateAsync: deleteCdn } = useMutation(
    useDeleteCdn({
      cdnUuid: row.uuid
    })
  )

  const handleDeleteCdn = async () => {
    try {
      await deleteCdn()
      toastManager.success("Cdn deleted")
    } catch (_error) {}
  }

  const cdnActionMenu = (
    <>
      <DropdownItem
        className="!justify-start flex items-center gap-xs"
        data-testid={`edit-btn-${row.uuid}`}
        type="button"
        onClick={() => {
          navigate({
            to: `${workspacePath}/cdn/${row.uuid}`
          })
        }}
      >
        <Icon icon={IconName.EDIT} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
        Edit
      </DropdownItem>

      {!row.playUuid && (
        <>
          <Divider />
          <Tooltip
            body="CDNs that are used in segments cannot be deleted"
            enabled={row.segmentList.length > 0}
            placement="bottom"
          >
            <DropdownItem
              className="!justify-start flex items-center gap-xs text-red-600"
              data-testid={`delete-btn-${row.uuid}`}
              disabled={row.segmentList.length > 0}
              type="button"
              onClick={() => {
                openConfirmationModal({
                  cancelBtnLabel: "Cancel",
                  children: (
                    <div>
                      <strong>{row.name}</strong> will be deleted.
                    </div>
                  ),
                  okBtnLabel: "Delete CDN",
                  onAccept: handleDeleteCdn,
                  title: "Delete Cdn ?",
                  variant: "destructive"
                })
              }}
            >
              <Icon icon={IconName.TRASH} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
              Delete
            </DropdownItem>
          </Tooltip>
        </>
      )}
    </>
  )

  return (
    <TR {...linkAttrs}>
      <TextCell searchRegExps={searchRegExps} text={row.name} />
      <TD>
        <div className="flex flex-row items-center">
          <div className="mr-xs flex font-medium font-monospace text-blue-600 text-sm">
            {row.pathwayId}
          </div>

          <Tooltip
            body={
              "Copy this id for integration in your streaming solution as “Content steering pathway / service location id"
            }
            placement="bottom"
          >
            {/* biome-ignore lint/a11y/noStaticElementInteractions: clickable non-interactive element */}
            <span
              className="contents"
              role="presentation"
              onClick={event => {
                event.stopPropagation()
                event.preventDefault()
              }}
            >
              <CopyToClipboardButton
                color={ButtonColor.SECONDARY}
                iconSize={IconSize.MEDIUM}
                shape={ButtonShape.UNCONTAIN}
                valueToCopy={row.pathwayId}
                withTitle={false}
              />
            </span>
          </Tooltip>
        </div>
      </TD>
      <TD>
        <SegmentListCell segmentList={row.segmentList} />
      </TD>
      <TD className="items-center justify-center">
        <CdnStatus qoEStatus={row.qoeStatus} />
      </TD>
      <TD className="!p-0 z-20 items-center">
        <div className="flex items-center justify-center">
          <DropdownButton
            body={cdnActionMenu}
            className="flex"
            color={ButtonColor.TERTIARY}
            data-testid={`more-options-btn-${row.uuid}`}
            floatingClassName="z-[10001]"
            hideCaret
            shape={ButtonShape.ICON}
            size={ButtonSize.SMALL}
            type="button"
          >
            <Icon icon={IconName.ELLIPSIS} size={IconSize.SMALL} type={IconVariant.SOLID} />
          </DropdownButton>
        </div>
      </TD>
    </TR>
  )
}

export default CdnTable
